/**
 * Reads the hotel chain's tax-module state off an API attributes payload.
 *
 * The backoffice hotel-chain read representation carries `tax_module_enabled`
 * (SPAZ2-3364), and both writers of the persisted app-config chain — the header
 * hotel-chain selector (chains-list row `attributes`) and the legacy current-user
 * bootstrap effect (the normalized chain sideloaded on the user's profile) — read it
 * from here so the fail-closed rule exists in exactly one place.
 *
 * The pinned `@spazious/client-backoffice` predates that backend change, so
 * `HotelChainReadAttributes` does not declare the field yet: the value is present at
 * runtime but not on the typed surface. Hence the `unknown` boundary parameter and the
 * type guards — no `as`, no client bump. Once a regenerated client declares the field
 * this narrow becomes redundant, never wrong.
 *
 * Absent, non-boolean or non-object input reports **disabled**: enabled is opt-in, so a
 * backend that predates the attribute (or a redux-persist session rehydrated from an
 * older build) degrades to "off" instead of throwing.
 */
export function readTaxModuleEnabled(attributes: unknown): boolean {
  if (typeof attributes !== 'object' || attributes === null) return false;
  if (!('taxModuleEnabled' in attributes)) return false;

  return attributes.taxModuleEnabled === true;
}

/**
 * Reads the tax-module state off an order's own sideloaded hotel chain, or reports that the
 * order does not carry one.
 *
 * Three-valued on purpose, and that is the whole point of it existing next to
 * `readTaxModuleEnabled`: a surface showing one specific order has to tell "this order's chain
 * has the module off" apart from "this payload never included the chain". The first is an
 * answer and must win over the header-selected chain; the second is silence and must fall back
 * to it. Collapsing them — which is what `readTaxModuleEnabled` does, by design, for the
 * app-config writers — would force every order whose payload omits the chain to render as
 * disabled.
 *
 * Why an order-owned flag is needed at all: an order can be open while the header sits on a
 * different chain. The order page retrieves by id with no chain filter, and the payments list
 * drops its selected-chain filter whenever it is scoped to an order, so a tax-enabled order is
 * reachable from a tax-disabled header chain. Reading the header flag there put the order page
 * and its amount bar on gross while the payment form fell back to net, which is an operator
 * recording the net and leaving the tax uncollected.
 *
 * `unknown` in, because the callers are normalized JSON:API payloads with no generated type and
 * because the pinned client does not declare `taxModuleEnabled` on the chain yet. Anything that
 * is not a real boolean is silence, not `false`: a non-boolean means the shape is not what this
 * reader thinks it is, and guessing "off" from it would be the fail-closed rule applied to the
 * wrong question.
 */
export function readOrderTaxModuleEnabled(order: unknown): boolean | undefined {
  if (typeof order !== 'object' || order === null) return undefined;
  if (!('hotelChain' in order)) return undefined;

  const hotelChain = order.hotelChain;
  if (typeof hotelChain !== 'object' || hotelChain === null) return undefined;
  if (!('taxModuleEnabled' in hotelChain)) return undefined;

  return typeof hotelChain.taxModuleEnabled === 'boolean' ? hotelChain.taxModuleEnabled : undefined;
}

export default readTaxModuleEnabled;
